import * as React from "react";
import { Drawer, IconButton, Box, Tooltip, Badge, Typography, Chip, Stack, Button } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { common } from "@mui/material/colors";
import CloseRounded from '@mui/icons-material/CloseRounded';
import { AppContext } from "AppContext";
import ClearAllIcon from '@mui/icons-material/ClearAll';
import { ActionTypes } from "reducer/actions";
import * as MDT from "models/mdt";
import Select from 'react-select';
import { addFilterFactory, addHierarchyFilterFactory } from "helpers/filterHelper";
import { FilterContext } from "models/core";
import { ArrowBackIos } from "@mui/icons-material";
import { QCCATClaims } from "qccat-ui";
import { getUserSettings } from "api/dashboardApi";
import { serverExists } from "helpers";
import Loader from "./filterloader";


enum LoadingAllDataState {
	FetchingData,
	Waiting,
	ErrorFetchingData,
	FetchedData
}

function Page(props: { filterDrawer: React.ReactNode, main: React.ReactNode, clearFilters: (dashboard: any) => any, computeChartData: (dashboard: any) => Promise<any>, loadData: (dashboard: any) => Promise<any> }) {
	const [openFilterDrawer, setOpenFilterDrawer] = React.useState(false);
	const [loadingAllDataState, setLoadingAllDataState] = React.useState<LoadingAllDataState>(LoadingAllDataState.Waiting);

	const { state, dispatch } = React.useContext(AppContext);

	const filterLength = Object.keys(state.page.dashboard.filters).length;

	React.useEffect(() => {

		const apiGetUserSettings = async () => {
			try {
				const result = await getUserSettings();
				return dispatch({ type: ActionTypes.ApiGetUserSettings, payload: result });
			}
			catch (error) {
				console.error(error);
			}
		}

		serverExists("https://qheps.health.qld.gov.au", exists => {
			return dispatch({ type: ActionTypes.ShowInternalLinks, payload: exists })
		});

		apiGetUserSettings();

	}, [])

	React.useEffect(() => {
		setLoadingAllDataState(LoadingAllDataState.Waiting);
	}, [props.main])

	const pageIsLoading =
		state?.page?.loading != null ? state.page.loading : true;

	const loadAllDataView = () => {
		const msgStyle = {
			display: "block", fontSize: "small", textAlign: "left", marginTop: 3, fontStyle: "italic"
		} as React.CSSProperties

		switch (loadingAllDataState) {
			case LoadingAllDataState.Waiting:
				return (
					<Tooltip title="Load all data">
						<>
							<Button onClick={() => {
								if (pageIsLoading)
									return;
								setLoadingAllDataState(LoadingAllDataState.FetchingData);
								dispatch({ type: ActionTypes.UserWantsToLoadMdtDashboard });
								return props.loadData(Object.assign({}, state.page.dashboard, { allData: true }))
									.then((value) => {
										setLoadingAllDataState(LoadingAllDataState.FetchedData);
										dispatch({ type: ActionTypes.UserLoadedMdtDashboard, payload: value })
									}).catch(e => {
										setLoadingAllDataState(LoadingAllDataState.ErrorFetchingData);
										console.error(e);
									});
							}}
								style={{
									backgroundColor: pageIsLoading ? ("#A9A9A9") : ("#1e88e5"),
									color: "white",
									cursor: pageIsLoading ? "not-allowed" : "pointer"
								}}>
								Load All Data
							</Button>
							<span style={msgStyle}>Warning: Loading all data can take several minutes, please do not refresh the page</span>
						</>
					</Tooltip>
				)
			case LoadingAllDataState.ErrorFetchingData:
				return (
					<span style={msgStyle}>Error: Unable to load all data at this time, please try again later</span>
				)

			case LoadingAllDataState.FetchingData:
				return (
					<span style={msgStyle}>In Progress: Loading all data can take several minutes, please do not refresh the page</span>
				)

			case LoadingAllDataState.FetchedData:
				return (
					<span style={msgStyle}>Success: All data was loaded successfully</span>
				)
		}
	}

	//to make multiselect filters with chips
	return (
		<>
			<Box sx={{ display: "flex" }}>
				<Box sx={{ width: 80, textAlign: "center"/*, position: "sticky", top: "205px", left: 0*/ }}>
					<Tooltip title="Show filters">
						<IconButton sx={{ position: "sticky", top: "105px", left: 0 }} onClick={() => !pageIsLoading && setOpenFilterDrawer(true)}>
							<ChevronRightIcon fontSize="large" sx={{ color: common.black }} />
						</IconButton>
					</Tooltip>

					<Badge badgeContent={filterLength} color="primary" sx={{ marginTop: 2, zIndex: 3, position: "sticky", top: "155px", left: 0 }}>
						<Tooltip title="Clear filters">
							<IconButton onClick={() => {
								if (!pageIsLoading) {
									dispatch({ type: ActionTypes.ComputingChartData });
									props.computeChartData(props.clearFilters(state.page.dashboard))
										.then(dashboard => dispatch({ type: ActionTypes.UserClearedFilters, payload: dashboard }));
								}

							}}>
								<ClearAllIcon fontSize="large" sx={{ color: common.black }} />
							</IconButton>
						</Tooltip>
					</Badge>

				</Box>
				<Box sx={{ flexGrow: 1 }}>
					{props.main}
				</Box>
			</Box>

			<Drawer
				anchor="left"
				variant="temporary"
				open={openFilterDrawer}
				PaperProps={{ style: { width: 450, paddingLeft: 10, position: 'relative', zIndex: 999 } }}
			>
				<Stack sx={{ marginTop: 10, padding: 2 }} spacing={3}>
					<div style={{ width: "100%" }}>
						<Typography style={{ fontSize: "1.5em", display: "inline" }}>Filters</Typography>
						<Box sx={{ textAlign: "right", display: "inline", marginLeft: "280px" }}>
							<Tooltip title="Close filters">
								<IconButton onClick={() => !pageIsLoading && setOpenFilterDrawer(false)}>
									<ArrowBackIos sx={{ color: common.black }} />
								</IconButton>
							</Tooltip>

							{props.loadData && state.userSettings.isAdmin &&
								loadAllDataView()}


						</Box>
					</div>

					{props.filterDrawer}

				</Stack>
			</Drawer>
		</>
	)
}

export default Page;